import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase_client';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [country, setCountry] = useState('MY');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countries, setCountries] = useState<any[]>([]);
  const [countryPhoneCode, setCountryPhoneCode] = useState('+60');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [invitationData, setInvitationData] = useState<any>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [referrerOrgId, setReferrerOrgId] = useState<string | null>(null);
  const [referrerOrgName, setReferrerOrgName] = useState<string | null>(null);
  const [loadingReferrer, setLoadingReferrer] = useState(false);

  //  Promo code
  const [promoCode, setPromoCode] = useState('');
  const [promoVerified, setPromoVerified] = useState<{ fullName: string; matchedCode: string } | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoLookupDone, setPromoLookupDone] = useState(false);

  useEffect(() => {
    const invitationId = searchParams.get('invitation') || searchParams.get('token');
    if (invitationId) {
      navigate(`/complete-profile?invitation=${invitationId}`, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const trimmed = promoCode.trim();
    if (!trimmed) {
      setPromoVerified(null);
      setPromoChecking(false);
      setPromoLookupDone(false);
      return;
    }
    setPromoChecking(true);
    setPromoLookupDone(false);
    const timer = setTimeout(async () => {
      const { data, error } = await supabase.rpc('verify_salesman_code', { p_code: trimmed });
      setPromoChecking(false);
      setPromoLookupDone(true);
      if (error || !data || !Array.isArray(data) || data.length === 0) {
        setPromoVerified(null);
        return;
      }
      const row = data[0] as { id: string; code: string; full_name: string };
      setPromoVerified({ fullName: row.full_name, matchedCode: row.code });
    }, 450);
    return () => clearTimeout(timer);
  }, [promoCode]);

  const fetchCountries = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('iso, name, country_phone_code')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching countries:', error);
        return;
      }

      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  }, []);

  // ✅ Add function to fetch invitation data
  const fetchInvitationData = useCallback(async (invitationId: string) => {
    setLoadingInvitation(true);
    try {
      const { data, error } = await supabase
        .from('user_invitations')
        .select(`
          id,
          email,
          org_id,
          role_id,
          organizations(name)
        `)
        .eq('id', invitationId)
        .eq('status', 'pending')
        .single();

      if (error) throw error;

      if (data) {
        setInvitationData(data);
        setEmail(data.email); // Pre-fill email
        // Hide organization fields if invited
        setOrganizationName('');
      } else {
        setMsg('Invalid or expired invitation link.');
      }
    } catch (err: any) {
      console.error('Error fetching invitation:', err);
      setMsg('Invalid or expired invitation link.');
    } finally {
      setLoadingInvitation(false);
    }
  }, []);

  // Add useEffect to fetch countries on component mount
  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // ✅ Add useEffect to check for invitation token
  useEffect(() => {
    const invitationId = searchParams.get('invitation');
    // Support both legacy `ref_org` and new public referral link `ref`
    const refOrgId = searchParams.get('ref') || searchParams.get('ref_org');
    
    if (invitationId) {
      fetchInvitationData(invitationId);
    }
    
    // Store referral org ID if present
    if (refOrgId) {
      setReferrerOrgId(refOrgId);
    }
  }, [searchParams, fetchInvitationData]);

  // ✅ Fetch referrer org name (so user can confirm referral applied)
  useEffect(() => {
    const refOrgId = searchParams.get('ref') || searchParams.get('ref_org');
    if (!refOrgId) {
      setReferrerOrgName(null);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoadingReferrer(true);
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('id, name')
          .eq('id', refOrgId)
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          console.error('Error fetching referrer org:', error);
          setReferrerOrgName(null);
          return;
        }

        setReferrerOrgName(data?.name || null);
      } catch (e) {
        if (!cancelled) {
          console.error('Error fetching referrer org (exception):', e);
          setReferrerOrgName(null);
        }
      } finally {
        if (!cancelled) setLoadingReferrer(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const geoDetectedRef = useRef(false);
  useEffect(() => {
    if (geoDetectedRef.current) return;
    geoDetectedRef.current = true;

    const cached = localStorage.getItem('detected_country_iso');
    if (cached === 'SG' || cached === 'MY') {
      setCountry(cached);
      const match = countries.find(c => c.iso === cached);
      if (match) setCountryPhoneCode(match.country_phone_code);
      return;
    }

    const detect = async () => {
      try {
        const res = await fetch('https://ipwho.is/', { signal: AbortSignal.timeout(4000) });
        const data = await res.json();
        const code = (data?.country_code || '').toUpperCase();
        if (code === 'SG' || code === 'MY') {
          localStorage.setItem('detected_country_iso', code);
          setCountry(code);
          const match = countries.find(c => c.iso === code);
          if (match) setCountryPhoneCode(match.country_phone_code);
        }
      } catch {
        // Keep default
      }
    };
    detect();
  }, [countries]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setLoading(true);

    // ✅ Validate password match
    if (password !== confirmPassword) {
      setMsg('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // 1. Create Auth User with phone number
      const { data: signData, error: signErr } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            phone: `${countryPhoneCode}${phoneNumber}`,
            display_name: displayName || email.split('@')[0],
            contact_number: `${countryPhoneCode}${phoneNumber}`,
            country: country,
            org_name: organizationName,
            org_country: country,
            org_address: address || null,
          }
        }
      });
      if (signErr) throw signErr;

      const user = signData.user;

      // Detect existing verified user: Supabase returns user with empty identities
      if (user && user.identities?.length === 0) {
        setMsg('An account with this email already exists. Please sign in instead.');
        setLoading(false);
        return;
      }

      // If email confirmation required, store data for later
      if (!user) {
        localStorage.setItem('pendingSignup', JSON.stringify({
          promoCode,
          organizationName,
          country,
          address,
          phoneNumber: `${countryPhoneCode}${phoneNumber}`,
          displayName,
          invitationId: searchParams.get('invitation'),
          referrerOrgId: searchParams.get('ref') || searchParams.get('ref_org')
        }));
        setMsg('Signup successful — please confirm your email before logging in.');
        setLoading(false);
        return;
      }

      // If user is immediately available, complete the setup
      await completeUserSetup(user.id, organizationName, country, phoneNumber, address, displayName, referrerOrgId, promoVerified?.matchedCode ?? null);
      
      setSuccessMessage('Account created successfully! Redirecting to signin...');
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        navigate('/signin');
      }, 3000);
    } catch (err: any) {
      setMsg(err?.message ?? JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  const completeUserSetup = async (userId: string, orgName: string, countryiso: string, phone: string, address: string, displayName: string, referrerOrgId: string | null, promoCode: string | null) => {
    // 2. Create User Profile
    const { error: profileErr } = await supabase
      .from('user_profiles')
      .upsert({
        id: userId,
        locale: 'en',
        contact_number: `${countryPhoneCode}${phone}`,
        country: countryiso,
        display_name: displayName || email.split('@')[0],
      }, { onConflict: 'id' });

    if (profileErr) throw profileErr;

    // 3. Check for invitations FIRST (by invitation token or by email)
    let invitation = null;
    
    // ✅ Check by invitation token if present
    const invitationId = searchParams.get('invitation');
    if (invitationId) {
      const { data: invitationData, error: invitationError } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('id', invitationId)
        .eq('status', 'pending')
        .single();
      
      if (!invitationError && invitationData) {
        invitation = invitationData;
      }
    }
    
    // If no invitation found by token, check by email
    if (!invitation) {
      const { data: invitations, error: invitationError } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('email', email)
        .eq('status', 'pending');

      if (invitationError) {
        console.error('Error checking invitations:', invitationError);
      }

      if (invitations && invitations.length > 0) {
        invitation = invitations[0];
      }
    }

    if (invitation) {
      // Invitation flow: finalize using RPC only.
      const { data: rpcData, error: rpcError } = await supabase.rpc('finalize_invited_user_profile', {
        p_invitation_id: invitation.id,
        p_display_name: displayName || email.split('@')[0],
        p_locale: 'en',
        p_contact_number: `${countryPhoneCode}${phone}`,
        p_country: countryiso
      });

      if (rpcError) throw rpcError;
      if (!rpcData?.success) {
        throw new Error(rpcData?.error || 'Failed to finalize invited profile');
      }

      console.log(`Invitation finalized via RPC for ${invitation.id}`);
      return; // Exit early - don't create their own organization
    }

    // 4. No invitations — always create a new organization (display names are not unique; never join an existing org by name)
    if (!invitationData) {
      const trimmedOrgName = orgName.trim();

      const { data: orgData, error: orgErr } = await supabase
        .from('organizations')
        .insert({
          name: trimmedOrgName,
          country_iso: countryiso,
          address: address,
          referred_by: referrerOrgId || null,
          referral_signup_date: referrerOrgId ? new Date().toISOString() : null,
          promo_code: promoCode || null
        })
        .select()
        .single();

      if (orgErr) throw orgErr;
      const orgId = orgData.id;

      const currencyMap: Record<string, string> = { MY: 'MYR', SG: 'SGD', HK: 'HKD' };
      const walletCurrency = currencyMap[countryiso];
      if (!walletCurrency) {
        console.error('No currency mapped for country:', countryiso);
      }
      let walletId: string | null = null;
      if (walletCurrency) {
        const { data: walletData, error: walletErr } = await supabase
          .from('wallets')
          .insert({ org_id: orgId, currency: walletCurrency })
          .select('id')
          .single();
        if (walletErr) {
          console.error('Error creating wallet:', walletErr);
        } else {
          walletId = walletData?.id || null;
        }
      }

      if (walletId && countryiso === 'SG') {
        const { count: existingSgOrgs } = await supabase
          .from('organization_members')
          .select('org_id, organizations!inner(country_iso)', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('role_id', 1)
          .eq('organizations.country_iso', 'SG')
          .neq('org_id', orgId);

        if (!existingSgOrgs || existingSgOrgs === 0) {
          const { error: bonusErr } = await supabase
            .from('wallet_transactions')
            .insert({
              wallet_id: walletId,
              kind: 'bonus',
              amount_numeric: 2,
              currency_balance: 0,
              reference_type: 'New sign up bonus',
              meta: { source: 'signup', country: 'SG' }
            });
          if (bonusErr) {
            console.error('Error creating sign-up bonus:', bonusErr);
          } else {
            await supabase.rpc('refresh_wallet_balances');
          }
        }
      }

      const { data: existingMember, error: memberCheckErr } = await supabase
        .from('organization_members')
        .select('role_id')
        .eq('org_id', orgId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existingMember && !memberCheckErr) {
        return;
      }

      const { error: memberErr } = await supabase
        .from('organization_members')
        .insert({
          org_id: orgId,
          user_id: userId,
          role_id: 1,
          is_active: true
        });

      if (memberErr) throw memberErr;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
    <div className="bg-white shadow-lg rounded-xl w-full max-w-4xl p-8">
      <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">Sign Up</h2>
      <p className="text-center text-gray-500 mb-6">Create your account to get started</p>

      {/* ✅ Referral banner */}
      {(referrerOrgId || referrerOrgName || loadingReferrer) && !invitationData && (
        <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="text-emerald-900 font-medium">
              {loadingReferrer ? (
                <>Referral detected — checking referrer…</>
              ) : referrerOrgName ? (
                <>Referred by <strong>{referrerOrgName}</strong></>
              ) : (
                <>Invalid referral code</>
              )}
            </p>
          </div>
        </div>
      )}

      {/* ✅ Show invitation info if present */}
      {invitationData && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-blue-800 font-medium">
              You've been invited to join <strong>{invitationData.organizations?.name}</strong>
            </p>
          </div>
        </div>
      )}

      {loadingInvitation && (
        <div className="text-center mb-4 text-gray-500">Loading invitation...</div>
      )}

        {msg && (
          <div
            className={`text-center mb-4 p-2 rounded ${
              msg.toLowerCase().includes('success')
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {msg}
          </div>
        )}

        <form onSubmit={handleSignUp} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-5">
              {/* ✅ Hide organization fields if invited */}
              {!invitationData && (
                <>
                  <div>
                    <label className="block text-gray-700 mb-1 font-medium">Organization Name</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                      placeholder="e.g., ABC Company Sdn Bhd"
                      required={!invitationData}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-1 font-medium">Address</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={address}
                      rows={5}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter organization address"
                      required={!invitationData}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-gray-700 mb-1 font-medium">Filing Country</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    const selectedCountry = countries.find(c => c.iso === e.target.value);
                    if (selectedCountry) {
                      setCountryPhoneCode(selectedCountry.country_phone_code);
                    }
                  }}
                  required
                >
                  {countries.map(c => (
                    <option key={c.iso} value={c.iso}>
                      {c.name}{c.iso === 'MY' ? ' (MBRS)' : c.iso === 'SG' ? ' (ACRA BizFinx)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">This determines which country's XBRL your organization will work for.</p>
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Promo Code (Optional)</label>
                <div className="relative">
                  <input
                    type="text"
                    className={`w-full border rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                      promoLookupDone && promoCode.trim() && !promoChecking && !promoVerified
                        ? 'border-amber-300 bg-amber-50/50'
                        : 'border-gray-300'
                    }`}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Enter code if you have one"
                    autoComplete="off"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center min-w-[4.5rem] justify-end">
                    {promoChecking && promoCode.trim() ? (
                      <span className="text-xs text-gray-500">Checking…</span>
                    ) : promoVerified ? (
                      <svg
                        className="w-6 h-6 text-green-600 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : null}
                  </div>
                </div>
                {promoLookupDone && promoCode.trim() && !promoChecking && !promoVerified && (
                  <p className="mt-1 text-xs text-amber-800">
                    This code was not found. You can still sign up without a promo code.
                  </p>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Email</label>
                <input
                  type="email"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='example@company.com'
                  required
                  disabled={!!invitationData} // ✅ Disable if invited
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Display Name</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g., John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Phone Number</label>
                <div className="flex">
                  <div className="relative">
                    <select
                      value={countryPhoneCode}
                      onChange={(e) => setCountryPhoneCode(e.target.value)}
                      className="appearance-none border border-gray-300 rounded-l-lg bg-white text-gray-700 pl-4 pr-8 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none w-24 cursor-pointer"
                    >
                      {countries.map(country => (
                        <option key={country.iso} value={country.country_phone_code}>
                          {country.country_phone_code}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <input
                    type="tel"
                    className="flex-1 border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                    value={phoneNumber}
                    onChange={(e) => {
                      // Only allow numbers
                      const phoneValue = e.target.value.replace(/[^0-9]/g, '');
                      setPhoneNumber(phoneValue);
                    }}
                    placeholder="123456789"
                    required
                  />
                </div>
                
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Password</label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-1 font-medium">Confirm Password</label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
            </div>
          </div>

          {/* Submit Button - Full Width */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </div>
        </form>
        <p className="text-center text-gray-500 mt-6 text-sm">
          Already have an account?{' '}
          <Link to="/signin" className="text-blue-600 hover:underline font-medium">
            Sign In
          </Link>
        </p>
      </div>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">Success!</h3>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              {successMessage}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/signin');
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}